import { useEffect, useRef, useState } from "react";
import { IMAGES } from "@/lib/api";

const EXT = (() => {
  if (typeof document === "undefined") return ".mp4";
  const v = document.createElement("video");
  return v.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') ? ".mp4" : ".webm";
})();
const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
const CLIPS = [1, 2, 3, 4, 5].map((n) =>
  IS_MOBILE && EXT === ".mp4" ? `/video/run-0${n}-mobile.mp4` : `/video/run-0${n}${EXT}`
);
const POSTER = "/video/run-01-poster.jpg";
const pad = (n) => String(n).padStart(2, "0");

export const HeroVideo = ({ sound = false }) => {
  const [reduced, setReduced] = useState(false);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(0);
  const [clip, setClip] = useState(0);
  const [prog, setProg] = useState(0);
  const refA = useRef(null);
  const refB = useRef(null);
  const containerRef = useRef(null);
  const refs = [refA, refB];
  const nextClip = (clip + 1) % CLIPS.length;

  useEffect(() => {
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false);
  }, []);

  useEffect(() => {
    if (reduced || failed) return;
    [0, 1].forEach((s) => {
      const el = refs[s].current;
      if (el) el.muted = !(sound && s === visible);
    });
    const v = refs[visible].current;
    if (v) {
      const p = v.play();
      if (p?.catch) p.catch(() => {});
    }
  }, [visible, sound, reduced, failed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = containerRef.current;
    if (!el || reduced || failed) return;
    const io = new IntersectionObserver(
      ([e]) => {
        const v = refs[visible].current;
        if (!v) return;
        if (e.isIntersecting) {
          const p = v.play();
          if (p?.catch) p.catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, reduced, failed]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    setProg(0);
    setClip(nextClip);
    setVisible(1 - visible);
  };

  if (reduced || failed) {
    return (
      <img
        src={IMAGES.hero}
        alt="Runner in motion, black and white"
        fetchPriority="high"
        className="img-bw h-full w-full object-cover opacity-60"
        data-testid="hero-video-fallback"
      />
    );
  }

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden" data-testid="hero-video">
      <div className="absolute inset-0 hero-zoom">
        {[0, 1].map((slot) => {
          const idx = slot === visible ? clip : nextClip;
          return (
            <video
              key={slot}
              ref={refs[slot]}
              src={CLIPS[idx]}
              muted
              playsInline
              preload={slot === visible ? "auto" : "metadata"}
              poster={slot === 0 && clip === 0 ? POSTER : undefined}
              aria-hidden="true"
              onEnded={slot === visible ? advance : undefined}
              onTimeUpdate={
                slot === visible
                  ? (e) => setProg(e.currentTarget.duration ? e.currentTarget.currentTime / e.currentTarget.duration : 0)
                  : undefined
              }
              onError={slot === visible ? () => setFailed(true) : undefined}
              className={`absolute inset-0 h-full w-full object-cover hero-video-grade transition-opacity duration-1000 ease-out ${
                slot === visible ? "opacity-60" : "opacity-0"
              }`}
              data-testid={`hero-video-slot-${slot}`}
            />
          );
        })}
      </div>
      <div
        className="absolute top-6 right-5 lg:right-10 z-10 flex items-center gap-4"
        data-testid="hero-video-hud"
      >
        <span className="font-mono-label text-white/50">CLUB TAPE {pad(clip + 1)}/{pad(CLIPS.length)}</span>
        <div className="hidden sm:flex gap-1.5" aria-hidden="true">
          {CLIPS.map((src, i) => (
            <div key={src} className="h-[2px] w-7 bg-white/20 overflow-hidden">
              <div
                className="h-full bg-white"
                style={{
                  width: i < clip ? "100%" : i === clip ? `${Math.round(prog * 100)}%` : "0%",
                  transition: "width 0.3s linear",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
